import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { Button, SquareButton } from "../../../ui/Button/Button";
import { Checkbox } from "../../../ui/Checkbox/Checkbox";
import { Image } from "../../../ui/Image/Image";
import { Input } from "../../../ui/Input/Input";
import { InputGroup } from "../../../ui/IputGroup/IputGroup";
import { CrossIcon } from "../../../ui/icons/CrossIcon";
import { MagnifyingGlassIcon } from "../../../ui/icons/MagnifyingGlassIcon";
import { VehicleMappingItem } from "../useVehicleMaping";

export type VehicleMappingData = {
  currentlySelected: number;
  currentlyShowing: number;
  selectNone: VoidFunction;
  selectVisible: VoidFunction;
  totalAvailable: number;
  totalSelected: number;
  vehicles: VehicleMappingItem[];
};

export const VehicleList: FunctionComponent<{
  criteria: string;
  data: VehicleMappingData;
  onAddVehicles?: VoidFunction;
  setCriteria(criteria: string): void;
}> = ({ criteria, data, onAddVehicles, setCriteria }) => {
  const { t } = useTranslation();

  return (
    <>
      <form
        onSubmit={(event): void => {
          event.preventDefault();
        }}
        className="pt-3 pb-2 px-4 flex gap-1"
      >
        {onAddVehicles && (
          <SquareButton
            aria-label="addVehicles"
            className="border border-edge !rounded-input shrink-0 w-11 md:w-14 md:text-xl"
            onClick={onAddVehicles}
            title={t("packageEditor.addVehicles")}
          >
            +
          </SquareButton>
        )}
        <InputGroup className="flex-1">
          <Input
            onChange={(event): void => {
              setCriteria(event.target.value);
            }}
            aria-label="criteria"
            before={<MagnifyingGlassIcon />}
            className="w-full"
            value={criteria}
          />
          <Button
            onClick={(): void => {
              setCriteria("");
            }}
            aria-label="clearCriteria"
            className="rounded-l-none"
            disabled={!criteria}
            type="button"
          >
            <CrossIcon />
          </Button>
        </InputGroup>
      </form>
      <div className="mx-6 mb-2 text-sm flex items-center justify-between gap-0.5">
        <span className="mr-auto">
          <span className="hidden sm:inline">
            {t("packageEditor.selected")}:{" "}
          </span>
          {[
            ...new Set([
              data?.totalSelected ?? 0,
              data?.vehicles.length ?? 0,
              data?.totalAvailable ?? 0,
            ]),
          ].join(" of ")}
        </span>
        <Button
          aria-label="selectAll"
          disabled={data?.currentlySelected === data?.currentlyShowing}
          onClick={data?.selectVisible}
        >
          {t("packageEditor.selectAllVehicles")}
        </Button>
        <Button
          aria-label="selectNone"
          disabled={!data?.currentlySelected}
          onClick={data?.selectNone}
        >
          {t("packageEditor.selectNoneVehicles")}
        </Button>
      </div>
      <div className="grid gap-1 mx-4">
        {data?.vehicles.map((vehicle) => {
          return <Vehicle key={`${vehicle.key}`} vehicle={vehicle} />;
        })}
      </div>
    </>
  );
};

const Vehicle: FunctionComponent<{
  vehicle: VehicleMappingItem;
}> = ({
  vehicle: {
    selected,
    toggle,
    vehicleImage,
    vehicleModel,
    vehicleModelId,
    vehicleName,
    vehicleSubmodelId,
  },
}) => {
  return (
    <label className="rounded-section border border-edge flex items-center p-2 pr-4 gap-2">
      <Image
        className={clsx(
          "w-14 h-14 bg-input rounded-section object-cover object-center",
          "text-transparent"
        )}
        alt="Model Image"
        errorClassName="opacity-40 blur-sm"
        fallbackSrc="/static/img/placeholder_car.png"
        src={vehicleImage}
      />
      <div className="mr-auto">
        <div>
          {vehicleName} {vehicleModel}
        </div>
        <div className="text-xs">
          Model ID: {vehicleModelId}, Submodel ID: {vehicleSubmodelId}
        </div>
      </div>
      <Checkbox
        checked={selected}
        name="vehicleSubmodelId"
        onChange={toggle}
        value={vehicleSubmodelId}
      />
    </label>
  );
};
